import { useState, useEffect } from "react";
import { Zap, Timer, ChevronLeft, ChevronRight, Play, Square } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

const FLUENCY_SPEEDS = [0.5, 1, 2, 3, 4, 5];
const ANSWER_DELAY_MIN = 0;
const ANSWER_DELAY_MAX = 10;

/**
 * Sight Vocabulary — Flashcard (one item + picture at a time, manual nav,
 * tap-to-hear) or Fluency Practice (silent auto-advancing recall drill).
 *
 * `hasSections`/`selectedSection` are optional — when passed (reading
 * modules 1-7 with admin-defined sections), the item bank is scoped down
 * to just the currently-selected section, matching Reading & Listening's
 * own scoping. When omitted, the caller has already pre-filtered `m.items`
 * itself (module 9's Learn shell does this today) and this component uses
 * the list as given.
 */
export default function LearnSightVocabulary({ module: m, hasSections, selectedSection }) {
  const { t } = useI18n();
  const [subMode, setSubMode] = useState("flashcard"); // "flashcard" | "fluency"
  const items = hasSections ? (m.items || []).filter((it) => it.rule === selectedSection) : (m.items || []);
  const scoped = { ...m, items };

  return (
    <div>
      {/* Sticky control bar — mirrors Test mode's sticky toolbar. */}
      <div className="sticky sticky-below-header z-30 -mx-4 sm:-mx-6 px-4 sm:px-6 pt-2 pb-2 mb-4 bg-[#FDFBF7]/95 backdrop-blur supports-[backdrop-filter]:bg-[#FDFBF7]/85" data-testid="sticky-toolbar">
        <div className="rounded-2xl bg-white border border-[#E7E0D2] shadow-sm p-2 flex items-center gap-2" data-testid="sight-vocab-sub-toggle">
          <button
            data-testid="sub-mode-flashcard"
            onClick={() => setSubMode("flashcard")}
            className={`flex-1 inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-full text-sm font-medium transition ${
              subMode === "flashcard" ? "bg-[#ECFDF5] text-[#064E3B] border border-[#A7F3D0]" : "text-[#4B5563] hover:text-[#1F2937] border border-transparent"
            }`}>
            <Zap className="w-4 h-4" /> {t("rd.sub_flashcard")}
          </button>
          <button
            data-testid="sub-mode-fluency"
            onClick={() => setSubMode("fluency")}
            className={`flex-1 inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-full text-sm font-medium transition ${
              subMode === "fluency" ? "bg-[#ECFDF5] text-[#064E3B] border border-[#A7F3D0]" : "text-[#4B5563] hover:text-[#1F2937] border border-transparent"
            }`}>
            <Timer className="w-4 h-4" /> {t("rd.sub_fluency_practice")}
          </button>
        </div>
      </div>

      {subMode === "flashcard"
        ? <FlashcardView key={selectedSection || "all"} module={scoped} />
        : <FluencyPracticeView key={selectedSection || "all"} module={scoped} />}
    </div>
  );
}

function FlashcardView({ module: m }) {
  const { t } = useI18n();
  const items = m.items;
  const [idx, setIdx] = useState(0);

  useEffect(() => { setIdx(0); }, [m.code]);

  if (!items.length) return null;
  const it = items[idx];

  return (
    <div className="rounded-3xl bg-white border border-[#E7E0D2] p-6 sm:p-10 parchment" data-testid="flashcard-view">
      <div className="flex flex-col items-center gap-6 py-6">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#4B5563]">
          {t("rd.flashcard_progress", { i: idx + 1, n: items.length })}
        </div>

        {it.picture_url ? (
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}${it.picture_url}`}
            alt={it.translit || it.ar}
            className="w-40 h-40 sm:w-52 sm:h-52 object-contain rounded-2xl border border-[#E7E0D2] bg-[#FDFBF7]"
            data-testid="flashcard-picture"
          />
        ) : null}

        {/* pb-[0.4em] is relative to this element's own (5xl/7xl) font-size, so
            it scales with the breakpoint automatically. Amiri Quran's descenders
            and diacritic marks render lower than a `leading-loose` line-height
            alone reserves room for at this display size, which is what was
            colliding with the translit label directly below. */}
        <div key={idx} data-testid="flashcard-item" className="font-quran text-5xl sm:text-7xl text-[#1F2937] leading-loose text-center flash-in pb-[0.4em]" dir="rtl" lang="ar">
          {it.ar}
        </div>
        {it.translit && (
          <div className="text-sm text-[#4B5563] italic">
            {it.translit}
          </div>
        )}

        <div className="flex flex-wrap justify-center gap-1.5 max-w-xl">
          {items.map((_, i) => (
            <span key={i} data-testid={`flash-dot-${i}`}
              className={`w-1.5 h-1.5 rounded-full transition ${i === idx ? "bg-[#064E3B] scale-[1.6]" : i < idx ? "bg-[#064E3B]/40" : "bg-[#E7E0D2]"}`} />
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button data-testid="flashcard-prev" onClick={() => setIdx((i) => (i - 1 + items.length) % items.length)}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-white border border-[#E7E0D2] hover:border-[#064E3B] text-[#1F2937] text-sm">
            <ChevronLeft className="w-4 h-4" /> <span className="hidden sm:inline">{t("rd.flashcard_prev")}</span>
          </button>
          <button data-testid="flashcard-next" onClick={() => setIdx((i) => (i + 1) % items.length)}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-white border border-[#E7E0D2] hover:border-[#064E3B] text-[#1F2937] text-sm">
            <span className="hidden sm:inline">{t("rd.flashcard_next")}</span> <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function FluencyPracticeView({ module: m }) {
  const { t } = useI18n();
  const items = m.items;
  const [speed, setSpeed] = useState(() => {
    const v = Number(localStorage.getItem("qf:fluency-practice-speed"));
    return FLUENCY_SPEEDS.includes(v) ? v : 1;
  });
  // Answer Delay — an optional hidden/blank waiting period inserted after a
  // word's reveal window and before the next word appears (teacher-paced
  // recall time). Defaults to 0, which reproduces the original behavior
  // exactly: the next word appears immediately after the reveal window.
  const [delay, setDelay] = useState(() => {
    const v = Number(localStorage.getItem("qf:fluency-practice-delay"));
    return Number.isFinite(v) && v >= ANSWER_DELAY_MIN && v <= ANSWER_DELAY_MAX ? v : 0;
  });
  const [idx, setIdx] = useState(0);
  const [running, setRunning] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => { localStorage.setItem("qf:fluency-practice-speed", String(speed)); }, [speed]);
  useEffect(() => { localStorage.setItem("qf:fluency-practice-delay", String(delay)); }, [delay]);
  useEffect(() => { setIdx(0); setRunning(false); setHidden(false); }, [m.code]);

  // Each cycle: reveal the current word for `speed` seconds, then — only if
  // an Answer Delay is set — hide it for `delay` seconds before advancing.
  // With delay at its default of 0 this is exactly the original interval:
  // advance to the next word immediately after the reveal window.
  useEffect(() => {
    if (!running || items.length === 0) { setHidden(false); return undefined; }
    let cancelled = false;
    let timer;
    const scheduleReveal = () => {
      setHidden(false);
      timer = setTimeout(() => {
        if (cancelled) return;
        if (delay > 0) {
          setHidden(true);
          timer = setTimeout(() => {
            if (cancelled) return;
            setIdx((i) => (i + 1) % items.length);
            scheduleReveal();
          }, delay * 1000);
        } else {
          setIdx((i) => (i + 1) % items.length);
          scheduleReveal();
        }
      }, speed * 1000);
    };
    scheduleReveal();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [running, speed, delay, items.length]);

  const decDelay = () => setDelay((d) => Math.max(ANSWER_DELAY_MIN, d - 1));
  const incDelay = () => setDelay((d) => Math.min(ANSWER_DELAY_MAX, d + 1));

  if (!items.length) return null;
  const it = items[idx];

  return (
    <div className="rounded-3xl bg-white border border-[#E7E0D2] p-6 sm:p-10 parchment" data-testid="fluency-practice-view">
      <div className="mb-6 flex justify-end">
        <div className="space-y-4">
          <div className="flex flex-col items-start gap-1.5">
            <div className="text-xs uppercase tracking-[0.22em] text-[#4B5563]">{t("rd.fluency_practice_speed_label")}</div>
            <div className="inline-flex items-center rounded-full border border-[#E7E0D2] overflow-hidden" data-testid="fluency-speed-control">
              {FLUENCY_SPEEDS.map((s) => (
                <button
                  key={s}
                  data-testid={`fluency-speed-${s}`}
                  onClick={() => setSpeed(s)}
                  disabled={running}
                  className={`px-3 py-1.5 text-xs font-medium tabular-nums transition disabled:cursor-not-allowed ${
                    speed === s ? "bg-[#064E3B] text-white" : "text-[#4B5563] hover:bg-[#ECFDF5] hover:text-[#1F2937]"
                  }`}>
                  {s}s
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-col items-start gap-1.5">
            <div className="text-xs uppercase tracking-[0.22em] text-[#4B5563]">{t("rd.fluency_practice_delay_label")}</div>
            <div className="inline-flex items-center rounded-full border border-[#E7E0D2] overflow-hidden" data-testid="answer-delay-control">
              <button
                data-testid="answer-delay-dec"
                onClick={decDelay}
                disabled={running || delay <= ANSWER_DELAY_MIN}
                aria-label="Decrease answer delay"
                className="px-3 py-1.5 text-xs font-medium text-[#4B5563] hover:bg-[#ECFDF5] hover:text-[#1F2937] transition disabled:opacity-40 disabled:cursor-not-allowed">
                −
              </button>
              <span data-testid="answer-delay-value" className="px-3 py-1.5 text-xs font-medium tabular-nums text-[#1F2937] min-w-[2.5rem] text-center">
                {delay}s
              </span>
              <button
                data-testid="answer-delay-inc"
                onClick={incDelay}
                disabled={running || delay >= ANSWER_DELAY_MAX}
                aria-label="Increase answer delay"
                className="px-3 py-1.5 text-xs font-medium text-[#4B5563] hover:bg-[#ECFDF5] hover:text-[#1F2937] transition disabled:opacity-40 disabled:cursor-not-allowed">
                +
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center gap-6 py-6">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#4B5563]">
          {t("rd.flashcard_progress", { i: idx + 1, n: items.length })}
        </div>
        {/* Same fix as FlashcardView above — extra em-relative clearance below
            the glyph so its descenders/marks don't collide with the progress
            dots sitting directly underneath. */}
        <div key={idx} data-testid="fluency-practice-item" className="font-quran text-5xl sm:text-7xl text-[#1F2937] leading-loose text-center flash-in pb-[0.4em]" dir="rtl" lang="ar">
          {hidden ? " " : it.ar}
        </div>
        <div className="flex flex-wrap justify-center gap-1.5 max-w-xl">
          {items.map((_, i) => (
            <span key={i} className={`w-1.5 h-1.5 rounded-full transition ${i === idx ? "bg-[#064E3B] scale-[1.6]" : i < idx ? "bg-[#064E3B]/40" : "bg-[#E7E0D2]"}`} />
          ))}
        </div>
        <button
          data-testid="fluency-practice-toggle"
          onClick={() => setRunning((r) => !r)}
          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium transition shadow-sm ${
            running ? "bg-[#DC2626] hover:bg-[#B91C1C] text-white" : "bg-[#064E3B] hover:bg-[#047857] text-white"
          }`}>
          {running ? <><Square className="w-4 h-4" /> {t("rd.fluency_practice_stop")}</> : <><Play className="w-4 h-4" /> {t("rd.fluency_practice_start")}</>}
        </button>
      </div>
    </div>
  );
}
